import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { fetchQuizQuestions, deleteQuestion } from '../vsecuriteSlice';
import { showSuccessToast, showErrorToast } from '../../../app/utils/helpers';
import Langs, { Localize } from '../../../app/lang/langs';

/** Brand orange */
const BRAND_ORANGE = '#FF9015';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * QuizQuestionsList
 *
 * Admin list of all questions for a given quiz.
 * Loads questions via fetchQuizQuestions({ id_quiz }) and renders each question
 * with its options. Provides Edit and Delete actions per row.
 *
 * Props:
 *   quizId         — numeric ID of the quiz whose questions to display
 *   onAddNew       — callback to show the blank QuestionForm
 *   onEditQuestion — callback(question) to open the QuestionForm in edit mode
 */
const QuizQuestionsList = ({ quizId, onAddNew, onEditQuestion, isPublished = false }) => {
    const dispatch = useDispatch();
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const mobile = isMobileRedux === 2 || isMobileScreen;

    const [questions, setQuestions]       = useState([]);
    const [loading, setLoading]           = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting]         = useState(false);

    const mounted = useRef(false);

    /* ── Load questions ────────────────────────────────────────────────────── */

    const loadQuestions = useCallback(async () => {
        if (!quizId) return;
        setLoading(true);
        try {
            const result = await dispatch(fetchQuizQuestions({ id_quiz: quizId })).unwrap();
            if (result?.responseData?.questions) {
                const sorted = [...result.responseData.questions].sort(
                    (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
                );
                setQuestions(sorted);
            } else {
                setQuestions([]);
            }
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUESTIONS_LOAD_ERROR', lang));
            setQuestions([]);
        } finally {
            setLoading(false);
        }
    }, [dispatch, quizId, lang]);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            loadQuestions();
        }
    }, [loadQuestions]);

    /* ── Delete handlers ───────────────────────────────────────────────────── */

    const openDeleteConfirm = (question) => setDeleteTarget(question);
    const closeDeleteConfirm = () => setDeleteTarget(null);

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await dispatch(deleteQuestion({ id: deleteTarget.id })).unwrap();
            showSuccessToast(Localize('VSEC_TOAST_QUESTION_DELETED', lang));
            closeDeleteConfirm();
            loadQuestions();
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUESTION_DELETE_ERROR', lang));
        } finally {
            setDeleting(false);
        }
    };

    /* ── Render ────────────────────────────────────────────────────────────── */

    return (
        <Box sx={{ mt: 0 }}>
            {/* ── Published lock banner ─────────────────────────────────────── */}
            {isPublished && (
                <Box
                    sx={{
                        mb: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: '10px',
                        background: 'rgba(255,144,21,0.08)',
                        border: '1px solid rgba(255,144,21,0.30)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                    }}
                >
                    <LockOutlinedIcon sx={{ color: BRAND_ORANGE, fontSize: '1rem', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.82rem', color: '#7a4a00', fontWeight: 500 }}>
                        <Langs str="VSEC_QUIZ_PUBLISHED_LOCKED" />
                    </Typography>
                </Box>
            )}

            {/* ── Top action bar ────────────────────────────────────────────── */}
            {!isPublished && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddNew}
                    fullWidth={mobile}
                    sx={{
                        backgroundColor: BRAND_ORANGE,
                        color: '#fff',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '12px',
                        minHeight: 48,
                        boxShadow: '0 2px 8px rgba(255,144,21,0.25)',
                        transition: `all 250ms ${EASE}`,
                        '&:hover': { backgroundColor: '#e6820f', boxShadow: '0 6px 20px rgba(255,144,21,0.35)', transform: 'translateY(-1px) scale(1.02)' },
                        '&:active': { transform: 'scale(0.98)' },
                    }}
                >
                    <Langs str="VSEC_BTN_ADD_QUESTION" />
                </Button>
            </Box>
            )}

            {/* ── Loading state ─────────────────────────────────────────────── */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: BRAND_ORANGE }} />
                </Box>
            )}

            {/* ── Empty state ───────────────────────────────────────────────── */}
            {!loading && questions.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        py: 6,
                        color: '#999',
                        border: '2px dashed rgba(0,40,128,0.1)',
                        borderRadius: '16px',
                        background: 'rgba(255,255,255,0.5)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                    }}
                >
                    <Typography sx={{ fontWeight: 500 }}>
                        <Langs str="VSEC_EMPTY_QUESTIONS" />
                    </Typography>
                </Box>
            )}

            {/* ── Question cards ────────────────────────────────────────────── */}
            {!loading && questions.map((q, index) => (
                <Box
                    key={q.id}
                    sx={{
                        background: 'rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 40, 128, 0.06)',
                        borderRadius: '16px',
                        mb: 2,
                        boxShadow: '0 1px 4px rgba(255, 144, 21, 0.04), 0 4px 16px rgba(0, 40, 128, 0.03)',
                        overflow: 'hidden',
                        transition: `all 300ms ${EASE}`,
                        '&:hover': {
                            boxShadow: mobile ? undefined : '0 6px 24px rgba(0,40,128,0.06), 0 2px 8px rgba(255,144,21,0.06)',
                            transform: mobile ? undefined : 'translateY(-1px)',
                        },
                    }}
                >
                    {/* Question header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: mobile ? 1 : 1.5,
                            px: mobile ? 1.5 : 2.5,
                            pt: mobile ? 1.5 : 2,
                            pb: 1.5,
                        }}
                    >
                        {/* Order badge */}
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: BRAND_ORANGE,
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                mt: 0.25,
                            }}
                        >
                            {index + 1}
                        </Box>

                        {/* Question text + type chip */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    color: '#1a1a2e',
                                    fontSize: mobile ? '0.88rem' : '0.95rem',
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {q.question_text}
                            </Typography>
                            <Box sx={{ mt: 0.75, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip
                                    label={
                                        q.type === 'single'
                                            ? Localize('VSEC_QUESTION_TYPE_SINGLE', lang)
                                            : Localize('VSEC_QUESTION_TYPE_MULTI', lang)
                                    }
                                    size="small"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                        backgroundColor: q.type === 'single' ? 'rgba(255,144,21,0.12)' : 'rgba(25,118,210,0.1)',
                                        color: q.type === 'single' ? BRAND_ORANGE : '#1976d2',
                                    }}
                                />
                                {q.explanation && (
                                    <Chip
                                        label={Localize('VSEC_CHIP_HAS_EXPLANATION', lang)}
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            backgroundColor: 'rgba(46,125,50,0.08)',
                                            color: '#2e7d32',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Actions – hidden for published quizzes */}
                        {!isPublished && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            <IconButton
                                size="small"
                                onClick={() => onEditQuestion(q)}
                                sx={{ color: '#1976d2', minWidth: 44, minHeight: 44 }}
                            >
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => openDeleteConfirm(q)}
                                sx={{ color: '#d32f2f', minWidth: 44, minHeight: 44 }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        )}
                    </Box>

                    {/* Options list */}
                    {Array.isArray(q.options) && q.options.length > 0 && (
                        <>
                            <Divider sx={{ mx: mobile ? 1.5 : 2.5 }} />
                            <Box sx={{ px: mobile ? 1.5 : 2.5, py: 1.5 }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                        gap: 0.75,
                                    }}
                                >
                                    {[...q.options]
                                        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                                        .map((opt) => (
                                            <Box
                                                key={opt.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    px: 1.25,
                                                    py: 0.6,
                                                    borderRadius: '8px',
                                                    background: opt.is_correct
                                                        ? 'rgba(46, 125, 50, 0.07)'
                                                        : 'rgba(0, 0, 0, 0.025)',
                                                    border: '1px solid',
                                                    borderColor: opt.is_correct
                                                        ? 'rgba(46, 125, 50, 0.25)'
                                                        : 'rgba(0, 0, 0, 0.07)',
                                                    minHeight: 36,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flexShrink: 0,
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: opt.is_correct ? '#2e7d32' : '#bdbdbd',
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.8rem',
                                                        color: opt.is_correct ? '#1b5e20' : '#555',
                                                        fontWeight: opt.is_correct ? 600 : 400,
                                                        flex: 1,
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {opt.option_text}
                                                </Typography>
                                                {opt.is_correct ? (
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.68rem',
                                                            fontWeight: 700,
                                                            color: '#2e7d32',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {Localize('VSEC_LABEL_CORRECT', lang)}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ))}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            ))}

            {/* ── Delete confirmation dialog ─────────────────────────────────── */}
            <Dialog
                open={Boolean(deleteTarget)}
                onClose={closeDeleteConfirm}
                fullWidth={mobile}
                maxWidth="xs"
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        borderRadius: mobile ? '18px 18px 0 0' : '18px',
                        background: '#fff',
                        minWidth: mobile ? undefined : 360,
                        boxShadow: '0 16px 48px rgba(0,40,128,0.12), 0 4px 16px rgba(255,144,21,0.06)',
                        ...(mobile && { position: 'fixed', bottom: 0, left: 0, right: 0, m: 0 }),
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    <Langs str="VSEC_DELETE_QUESTION_TITLE" />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Langs str="VSEC_DELETE_QUESTION_MSG" />
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, flexDirection: mobile ? 'column' : 'row', gap: mobile ? 1 : 0 }}>
                    <Button
                        onClick={closeDeleteConfirm}
                        variant="outlined"
                        disabled={deleting}
                        fullWidth={mobile}
                        sx={{ minHeight: 44, textTransform: 'none', borderRadius: '10px' }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        fullWidth={mobile}
                        sx={{ minHeight: 44, textTransform: 'none', borderRadius: '10px' }}
                    >
                        {deleting ? <Langs str="VSEC_BTN_DELETING" /> : <Langs str="VSEC_BTN_DELETE" />}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QuizQuestionsList;
